import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Alert, 
  AlertTitle, 
  Button, 
  Snackbar, 
  LinearProgress,
  Box,
  Typography,
  IconButton,
  Portal
} from '@mui/material';
import { 
  Refresh as RefreshIcon, 
  Close as CloseIcon,
  CloudOff as OfflineIcon,
  SystemUpdate as UpdateIcon
} from '@mui/icons-material';
import { useServiceWorkerUpdates } from '../../hooks/useServiceWorkerUpdates';

export const PWAUpdateNotification = () => {
  const { t } = useTranslation();
  const { 
    isUpdateAvailable, 
    isInstalling, 
    isOffline, 
    needRefresh, 
    updateServiceWorker 
  } = useServiceWorkerUpdates();
  
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [showOfflineNotification, setShowOfflineNotification] = useState(false);
  const [showInstallProgress, setShowInstallProgress] = useState(false);

  // Show update prompt when update is available
  useEffect(() => {
    if (isUpdateAvailable && !isInstalling) {
      setShowUpdatePrompt(true);
    }
  }, [isUpdateAvailable, isInstalling]);

  // Show offline notification
  useEffect(() => {
    setShowOfflineNotification(isOffline);
  }, [isOffline]);

  // Show install progress
  useEffect(() => {
    setShowInstallProgress(isInstalling);
  }, [isInstalling]);

  // Refresh page when update is applied
  useEffect(() => {
    if (needRefresh) {
      window.location.reload();
    }
  }, [needRefresh]);

  const handleUpdateNow = async () => {
    setShowUpdatePrompt(false);
    setShowInstallProgress(true);
    try {
      await updateServiceWorker();
    } catch (error) {
      console.error('Error updating service worker:', error);
      setShowInstallProgress(false);
    }
  };

  const handleUpdateLater = () => {
    setShowUpdatePrompt(false);
  };

  const handleCloseOffline = () => {
    setShowOfflineNotification(false);
  };

  return (
    <Portal>
      {/* Update Available Prompt */}
      <Snackbar
        open={showUpdatePrompt}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          severity="info"
          icon={<UpdateIcon />}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleUpdateNow}
                startIcon={<RefreshIcon />}
              >
                {t('pwa.updateNow')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleUpdateLater}
              >
                {t('pwa.updateLater')}
              </Button>
            </Box>
          }
          sx={{ 
            maxWidth: 600,
            '& .MuiAlert-message': { flex: 1 }
          }}
        >
          <AlertTitle>{t('pwa.updateAvailable')}</AlertTitle>
          {t('pwa.updateMessage')}
        </Alert>
      </Snackbar>

      {/* Installing Update Progress */}
      <Snackbar
        open={showInstallProgress}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          severity="info"
          icon={<RefreshIcon className="animate-spin" />}
          sx={{ maxWidth: 400 }}
        >
          <Box>
            <Typography variant="body2" gutterBottom>
              {t('pwa.installing')}
            </Typography>
            <LinearProgress sx={{ mt: 1 }} />
          </Box>
        </Alert>
      </Snackbar>

      {/* Offline Notification */}
      <Snackbar
        open={showOfflineNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{ zIndex: 9998 }}
      >
        <Alert
          severity="warning"
          icon={<OfflineIcon />}
          action={
            <IconButton
              size="small"
              onClick={handleCloseOffline}
              color="inherit"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{ maxWidth: 400 }}
        >
          <AlertTitle>{t('pwa.offline')}</AlertTitle>
          {t('pwa.offlineMessage')}
        </Alert>
      </Snackbar>
    </Portal>
  );
};